import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CoreService } from '@charlesschwab/sac-ui-core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, takeUntil } from 'rxjs/operators';
import { AccountInfoState } from '../../account-info-header/models/AccountInfoState';
import { selectInfo } from '../../account-info-header/reducers';
import { EAuthInfo } from '../../shared/models/EAuthInfo';
import { PaidForDetails } from '../../shared/models/TrustFieldsRequestInfo';
import { selectAcknowledgementDateTime, selectEAuthInfo } from '../../shared/reducers';
import { StatusViewService } from '../../shared/services/status-view.service';
import { isStandingInstruction } from '../../shared/types/StandingInstruction';
import { StandingAuthWireActions } from '../../standing-authorizations/actions';
import { StandingAuthorizationForm, WireStandingAuthDetails } from '../../standing-authorizations/models/standingAuthorizationForm';
import { selectStagedStandingAuthForm, selectStandingAuthForm, selectStandingAuthWireInfo } from '../../standing-authorizations/reducers';
import { selectIsCreateStandingAuth, selectIsCreateTransaction } from "../../shared/reducers";
import { TransactionFormActions, WireActions } from '../actions';
import {
  TransactionForm,
  WireExtraPrompts,
  WireTransactionDetails
} from '../models/form/TransactionForm';
import { PaidFor } from "../models/ptrs/PaidFor";
import { ServiceError, ServiceErrorWithList } from '../models/shared/ServiceError';
import { WireInfo } from '../models/wire/WireInfo';
import { WireInputRequest } from '../models/wire/WireInputRequest';
import { WireInputResponseV3 } from '../models/wire/WireInputResponseV3';
import { WireInquiryResponse } from '../models/wire/WireInquiryResponse';
import {
  selectIsFromEdit,
  selectPtrsInfoPaidFor,
  selectStagedTransactionForm,
  selectSubmitAndCreateToggledOn,
  selectTransactionForm,
  selectWireInfo
} from '../reducers';
import { WireService } from '../services/wire.service';
import { currencyStringToNumber } from '../utilities/currencyStringToNumber';
import { selectModifiedFirstPartyPayeesFormatted } from '../../modified-first-parties/reducers';
import { ModifiedPayeeNames } from '../../modified-first-parties/models/FirstParties';
import { WireVerifyRequestV3 } from '../models/wire/WireVerifyRequestV3';
import { WireVerifyResponseV3 } from '../models/wire/WireVerifyResponseV3';
import { WireAcknowledgeRequestV3 } from '../models/wire/WireAcknowledgeRequestV3';
import { WireAcknowledgeResponseV3 } from '../models/wire/WireAcknowledgeResponseV3';
import { selectInitReleaseTransactionInContext } from '../../initiate-and-release/reducers';
import { DraftListAttributes } from '../../initiate-and-release/Models/Drafts/DraftResponse';
import { WireDetailsRequest } from '../models/wire/WireDetailsRequest';
import { DetailsResponse } from '../models/wire/WireInfo';
import { WireDetailsApiResponse } from '../models/wire/WireDetailsApiResponse';
import { getTradeDate } from '../../shared/utilities/datetime-utils';
import { RelatedCustomer } from '../models/internal-transfer/RelatedCustomer';
import { EAuthModel } from '../models/internal-transfer/EAuthModel';
import { ToastrService } from 'ngx-toastr';
import { Country } from '../models/shared/Country';
import { selectIsCreateSloaToggleFlagEnabled } from '../../feature-flags/reducers';
import { selectHasAccessInitiateMoneyMovement } from '../../authorize/reducers';
import { ValidateWireFlagsService } from '../../shared/services/validate-WireFlags.service';
import { isPalAccountFlow } from '../../shared/utilities/AccountFlow';

@Injectable()
export class WireEffects {
  constructor(
    private actions$: Actions,
    private wireService: WireService,
    private statusViewService: StatusViewService,
    private store: Store,
    private datepipe: DatePipe,
    private toastr: ToastrService,
    private coreService: CoreService,
    private validateWireFlagsService: ValidateWireFlagsService
  ) { }

  destroy$: Subject<boolean> = new Subject<boolean>();
  hasIsoWireEnrolledFirm: boolean = false;

  callWireInputV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWireInputV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEdit),
      ]),
      switchMap(([payload, accountInfo, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }

        const splitAuthId = payload.standingAuthId.split('-');
        let cashieringRequestId = '';
        let cashieringRequestSecondId = '';

        if (splitAuthId.length > 1) {
          cashieringRequestId = splitAuthId[1];
          cashieringRequestSecondId = splitAuthId[2];
        }

        const wireInputRequest: WireInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
          sloaId: {
            cashieringRequestId: cashieringRequestId,
            cashieringRequestSecondId: cashieringRequestSecondId,
          },
          isCreateSloa: false,
          isBundledSloa: false,
        };
        return this.wireService.callWireInputV3(wireInputRequest, !!payload.isPalAccountInContext).pipe(
          map((wireInputResponseV3: WireInputResponseV3) => {
            return WireActions.wireInputCallSuccessfulV3({ response: wireInputResponseV3 });
          }),
          catchError((err: ServiceError) =>
            of(WireActions.wireInputFailedV3(err))
          )
        );
      })
    )
  );
  
  callWireInputV3TemplateFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWireInputV3TemplateFlow),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectIsFromEdit),
      ]),
      switchMap(([_payload, accountInfo, isFromEdit]) => {
        if (isFromEdit) {
          return of(TransactionFormActions.editLoading());
        }

        const wireInputRequest: WireInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
          sloaId: {
            cashieringRequestId: '',
            cashieringRequestSecondId: '',
          },
          isCreateSloa: false,
          isBundledSloa: false,
        };
        return this.wireService.callWireInputV3(wireInputRequest).pipe(
          map((wireInputResponseV3) => {
            return WireActions.wireInputCallV3TemplateFlowSuccessful({ response: wireInputResponseV3 });
          }),
          catchError((err: ServiceError) =>
            of(WireActions.wireInputV3TemplateFlowFailed(err))
          )
        );
      })
    )
  );

  callStandingAuthWireInputV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthWireActions.callWireInputV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo)
      ]),
      switchMap(([_, accountInfo]) => {
        // TODO: check isFromEdit value
        let cashieringRequestId = '';
        let cashieringRequestSecondId = '';

        const wireInputRequest: WireInputRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount,
          sloaId: {
            cashieringRequestId: cashieringRequestId,
            cashieringRequestSecondId: cashieringRequestSecondId,
          },
          isCreateSloa: true,
          isBundledSloa: false,
        };
        return this.wireService.callWireInputV3(wireInputRequest).pipe(
          map((wireInputResponseV3) => {
            return StandingAuthWireActions.wireInputCallSuccessfulV3(wireInputResponseV3);
          }),
          catchError((err: ServiceError) =>
            of(StandingAuthWireActions.wireInputFailedV3(err))
          )
        );
      })
    )
  );

  callWireVerifyV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callWiresVerifyV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectTransactionForm),
        this.store.select(selectWireInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, modifiedPayeeList, paidFor, initReleaseTransactionInContext]) => {
        var request = this.createWireVerifyRequestV3(
          accountInfo,
          transactionForm,
          wireInfo,
          modifiedPayeeList,
          paidFor,
          initReleaseTransactionInContext
        );
        const isPalAccount = this.isPalAccountInContext();
        if (isPalAccount) {
          request.isFirstPartyWpfac = !request.isThirdParty && !!accountInfo.pfaDetails?.hasCustodyWPFARole;
        }
        return this.wireService.callWireVerifyV3(request, isPalAccount).pipe(
          map((response: WireVerifyResponseV3) => {
            return WireActions.verifyV3CallSuccessful({response: response, inrReleaseFlowFromTable: !!initReleaseTransactionInContext?.draftId});
          }),
          catchError((err: ServiceErrorWithList) => {
            return of(WireActions.verifyV3CallFailed(err))
          })
        );
      })
    )
  );

  callWireSloaVerifyV3FromCreateTransaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callWireSloaVerifyV3FromCreateTransaction),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectTransactionForm),
        this.store.select(selectWireInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, modifiedPayeeList, paidFor, initReleaseTransactionInContext]) => {
        var request = this.createWireSloaVerifyRequestV3FromCreateTransaction(
          accountInfo,
          transactionForm,
          wireInfo,
          modifiedPayeeList,
          paidFor,
          initReleaseTransactionInContext
        );
        const isPalAccount = this.isPalAccountInContext();
        if (isPalAccount) {
          request.isFirstPartyWpfac = !request.isThirdParty && !!accountInfo.pfaDetails?.hasCustodyWPFARole;
        }
        return this.wireService.callWireVerifyV3(request, isPalAccount).pipe(
          map((response: WireVerifyResponseV3) => {
            return WireActions.callWireSloaVerifyV3FromCreateTransactionSuccessful(response);
          }),
          catchError((err: ServiceErrorWithList) => {
            this.toastr.error('Standing authorization creation currently unavailable.');
            return of(WireActions.callWireSloaVerifyV3FromCreateTransactionFailed(err));
          })
        );
      })
    )
  );

  callWireSloaVerifyV3FromCreateTransactionCustomRulesSplit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TransactionFormActions.callWireSloaVerifyV3FromCreateTransactionCustomRulesSplit),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectTransactionForm),
        this.store.select(selectWireInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, modifiedPayeeList, paidFor, initReleaseTransactionInContext]) => {
        var request = this.createWireSloaVerifyRequestV3FromCreateTransaction(
          accountInfo,
          transactionForm,
          wireInfo,
          modifiedPayeeList,
          paidFor,
          initReleaseTransactionInContext,
          false
        );
        const isPalAccount = this.isPalAccountInContext();
        if (isPalAccount) {
          request.isFirstPartyWpfac = !request.isThirdParty && !!accountInfo.pfaDetails?.hasCustodyWPFARole;
        }
        return this.wireService.callWireVerifyV3(request, isPalAccount).pipe(
          map((response: WireVerifyResponseV3) => {
            return StandingAuthWireActions.verifyV3CallSuccessful({response: response, inrReleaseFlowFromTable: false})
          }),
          catchError((err: ServiceErrorWithList) => {
            this.toastr.error('Standing authorization creation currently unavailable.');
            return of(StandingAuthWireActions.verifyV3CallFailed(err));
          })
        );
      })
    )
  );

  callStandingAuthWireVerifyV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthWireActions.callWiresVerifyV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStandingAuthForm),
        this.store.select(selectStandingAuthWireInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, modifiedPayeeList, initReleaseTransactionInContext]) => {
        var request = this.createStandingAuthVerifyRequestV3(
          accountInfo,
          transactionForm,
          wireInfo,
          modifiedPayeeList,
          initReleaseTransactionInContext
        );
        const isPalAccount = this.isPalAccountInContext();
        if (isPalAccount) {
          request.isFirstPartyWpfac = !request.isThirdParty && !!accountInfo.pfaDetails?.hasCustodyWPFARole;
        } 
        return this.wireService.callWireVerifyV3(request, isPalAccount).pipe(
          map((response: WireVerifyResponseV3) => {
            return StandingAuthWireActions.verifyV3CallSuccessful({response: response, inrReleaseFlowFromTable: !!initReleaseTransactionInContext?.draftId});
          }),
          catchError((err: ServiceErrorWithList) => {
            return of(StandingAuthWireActions.verifyV3CallFailed(err))
          })
        );
      })
    )
  );

  callWireAcknowledgeV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callAcknowledgeV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectWireInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectPtrsInfoPaidFor),
        this.store.select(selectEAuthInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionInContext),
        this.store.select(selectIsCreateSloaToggleFlagEnabled),
        this.store.select(selectHasAccessInitiateMoneyMovement),
        this.store.select(selectSubmitAndCreateToggledOn),
        this.store.select(selectStandingAuthWireInfo),
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, acknowledgementConsentDateTime, paidForInfo, eAuthInfo, modifiedPayeeList, initReleaseTransactionInContext, isCreateSloaToggleFlagEnabled, hasAccessInitiateMoneyMovement, submitAndCreateToggledOn, standingAuthWireInfo]) => {
        const shouldUseMainWireAcknowledgeFlowForSplitCustomRules =
          isCreateSloaToggleFlagEnabled
          && hasAccessInitiateMoneyMovement
          && submitAndCreateToggledOn
          && !!wireInfo?.authResponse?.canRelease
          && !wireInfo?.authResponse?.pendForRelease
          && !!standingAuthWireInfo?.authResponse?.canRelease
          && !standingAuthWireInfo?.authResponse?.pendForRelease;

        // When both transactions can release, keep the existing single-acknowledge
        // behavior by sending one request with both sets of case identifiers.
        const wireInfoForAcknowledge = shouldUseMainWireAcknowledgeFlowForSplitCustomRules
          ? {
              ...wireInfo,
              caseKey: this.mergeSplitWireCaseValue(wireInfo?.caseKey, standingAuthWireInfo?.caseKey),
              caseId: this.mergeSplitWireCaseValue(wireInfo?.caseId, standingAuthWireInfo?.caseId),
            }
          : wireInfo;

        var request = this.createWireAcknowledgeRequestV3(
          accountInfo,
          transactionForm,
          wireInfoForAcknowledge,
          acknowledgementConsentDateTime,
          paidForInfo,
          eAuthInfo,
          modifiedPayeeList,
          initReleaseTransactionInContext,
          (isCreateSloaToggleFlagEnabled && hasAccessInitiateMoneyMovement && submitAndCreateToggledOn && !shouldUseMainWireAcknowledgeFlowForSplitCustomRules)
        );
        return this.wireService.callWireAcknowledgeV3(request, this.isPalAccountInContext()).pipe(
          map((response: WireAcknowledgeResponseV3) => {
            return WireActions.acknowledgeCallV3Successful(response);
          }),
          catchError((err: ServiceErrorWithList) =>
            of(WireActions.acknowledgeCallV3Fail(err))
          )
        );
      })
    )
  );

  private mergeSplitWireCaseValue(primaryValue?: string, secondaryValue?: string): string {
    const uniqueValues = [...new Set(
      [primaryValue, secondaryValue]
        .filter(Boolean)
        .flatMap(value => value!.split(',').map(item => item.trim()).filter(Boolean))
    )];

    return uniqueValues.join(', ');
  }

  callStandingAuthWireAcknowledgeV3CustomRulesSplit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthWireActions.callAcknowledgeV3ForCustomRules),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedTransactionForm),
        this.store.select(selectStandingAuthWireInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectEAuthInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, acknowledgementConsentDateTime, eAuthInfo, modifiedPayeeList, initReleaseTransactionInContext]) => {
        var request = this.createStandingAuthWireAcknowledgeRequestV3ForCustomRulesSplitTransaction(
          accountInfo,
          transactionForm,
          wireInfo,
          acknowledgementConsentDateTime,
          eAuthInfo,
          modifiedPayeeList,
          initReleaseTransactionInContext
        );
        return this.wireService.callWireAcknowledgeV3(request, this.isPalAccountInContext()).pipe(
          map((response: WireAcknowledgeResponseV3) => {
            return StandingAuthWireActions.acknowledgeCallV3Successful(response);
          }),
          catchError((err: ServiceErrorWithList) =>
            of(StandingAuthWireActions.acknowledgeCallV3Fail(err))
          )
        );
      })
    )
  );
  callStandingAuthWireAcknowledgeV3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StandingAuthWireActions.callAcknowledgeV3),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectStagedStandingAuthForm),
        this.store.select(selectStandingAuthWireInfo),
        this.store.select(selectAcknowledgementDateTime),
        this.store.select(selectEAuthInfo),
        this.store.select(selectModifiedFirstPartyPayeesFormatted),
        this.store.select(selectInitReleaseTransactionInContext)
      ]),
      exhaustMap(([_, accountInfo, transactionForm, wireInfo, acknowledgementConsentDateTime, eAuthInfo, modifiedPayeeList, initReleaseTransactionInContext]) => {
        var request = this.createStandingAuthWireAcknowledgeRequestV3(
          accountInfo,
          transactionForm,
          wireInfo,
          acknowledgementConsentDateTime,
          eAuthInfo,
          modifiedPayeeList,
          initReleaseTransactionInContext
        );
        return this.wireService.callWireAcknowledgeV3(request, this.isPalAccountInContext()).pipe(
          map((response: WireAcknowledgeResponseV3) => {
            return StandingAuthWireActions.acknowledgeCallV3Successful(response);
          }),
          catchError((err: ServiceErrorWithList) =>
            of(StandingAuthWireActions.acknowledgeCallV3Fail(err))
          )
        );
      })
    )
  );

  callWireInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callInquiry),
      concatLatestFrom(() => [
        this.store.select(selectInfo)
      ]),
      switchMap(([action, accountInfo]) => {
        let payload: any = {
          cashieringRequestId: action.payload.cashieringRqstId,
          cashieringRqstSecondId: action.payload.cashieringRqstSecondId,
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
          transactionType: action.transactionType,
          isFromView: true
        };

        let isThirdParty: boolean = false;
        if (isStandingInstruction(action.payload)) {
          isThirdParty = action.payload.isThirdParty;
        } else {
          isThirdParty = action.payload.thirdPartyInd;
        }

        return this.statusViewService.callWireInquiry(payload).pipe(
          map((wireInquiryResponse: WireInquiryResponse) => {
            return WireActions.inquiryCallSuccessful({ wireInquiryResponse: { ...wireInquiryResponse, isThirdPartyTransaction: isThirdParty } });
          }),
          catchError((err: ServiceError) => of(WireActions.inquiryCallFailed(err))
          )
        );
      })
    )
  );

  createStandingAuthVerifyRequestV3(accountInfo: AccountInfoState, form: StandingAuthorizationForm, wireInfo: WireInfo, modifiedPayeeList: ModifiedPayeeNames[], initReleaseTransactionInContext: DraftListAttributes): WireVerifyRequestV3 {
    let transactionDetails = form.standingAuthorizationDetails as WireStandingAuthDetails;
    let isIra = accountInfo.classifications.isIra;
    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';
    let isTwoStep = transactionDetails.intermediarySelection === 'yes';
    let request: WireVerifyRequestV3 = {
      draftId: initReleaseTransactionInContext ? initReleaseTransactionInContext.draftId ?? '' : '',
      isCreateSloa: true,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate: DateTime.now().toFormat('yyyy-MM-dd'),
      frequency: 'ONREQST',
      amount: 0,
      isThirdParty: transactionDetails.recipient === 'thirdParty',
      isFirstPartyModified: this.isFirstPartyModifiedIso(
        transactionDetails.recipient,
        transactionDetails.name,
        transactionDetails.isoModifiedPayeeName ?? '',
        modifiedPayeeList,
        false,
        initReleaseTransactionInContext
      ),
      isEscrow: transactionDetails.isEscrow,
      isUseSloa: false,
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      beneficiaryAccount: wireInfo?.isIrsAba ? '' : transactionDetails.toAccount,
      beneficiaryBankId: wireInfo.isFedWire ? transactionDetails.aba : transactionDetails.bankID,
      clientAttested: true,
      beneficiary: {
        partyRole: "Cdtr",
        partySequenceNo: 0,
        partyType: "Individual",
        identifierList: [
          {
            identifierType: "Account",
            identifierValue: transactionDetails.toAccount
          }
        ],
        name: transactionDetails.name,
        postalAddress: {
          department: transactionDetails.receivingAccountHolderDepartment,
          streetName: transactionDetails.receivingAccountHolderStreetName,
          buildingNumber: transactionDetails.receivingAccountHolderStreetNumber,
          buildingName: transactionDetails.receivingAccountHolderBuildingName,
          floor: transactionDetails.receivingAccountHolderFloor,
          room: transactionDetails.receivingAccountHolderApartmentSuiteEtc,
          postCode: transactionDetails.receivingAccountHolderZip,
          townName: transactionDetails.receivingAccountHolderCity,
          townLocationName: transactionDetails.receivingAccountHolderTownLocationName,
          districtName: transactionDetails.receivingAccountHolderDistrict,
          countrySubDivision: transactionDetails.receivingAccountHolderState,
          country: transactionDetails.receivingAccountHolderCountry,
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      },
      beneficiaryBank: {
        fiRole: isTwoStep ? "IntrmyAgt" : "CdtrAgt",
        fiSequenceNo: isTwoStep ? 1 : 0,
        identifierList: [
          {
            identifierType: "USABA",
            identifierValue: transactionDetails.aba
          }
        ],
        name: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankName) ? wireInfo?.receivingBankDetailsIso?.recBankName :  transactionDetails.receivingBankName1,
        postalAddress: {
          department: "",
          streetName: "",
          buildingNumber: "",
          buildingName: "",
          floor: "",
          room: "",
          postCode: "",
          townName: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName) ? wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName : transactionDetails.receivingBankCity,
          townLocationName: "",
          districtName: "",
          countrySubDivision: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision) ? wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision : transactionDetails.receivingBankState,
          country: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.country) ? this.getCountryCode(wireInfo?.countryList, wireInfo?.receivingBankDetailsIso?.recBankAddress?.country) : transactionDetails.receivingBankCountry,
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        },
      },
      taxRemittance: {
        taxId: transactionDetails.taxPayerId ?? null,
        taxMonthCode: transactionDetails.taxMonth ?? null,
        taxTypeCode: transactionDetails.taxTypeCode ?? null,
        taxYear: transactionDetails.taxYear ?? null,
        additionalInfo: transactionDetails.transmissionNotes ?? null
      },
      isIrsAba: wireInfo?.isIrsAba ?? false,
      note: transactionDetails.transmissionNotes ? transactionDetails.transmissionNotes.trim() : '',
      isProcessDateExtended: wireInfo.processDateFields.isExtendedProcessingWindow,
      caseDetails: {
        caseKey: wireInfo.caseKey ?? '',
        caseId: wireInfo.caseId ?? '',
        isElectronicAuthorization: initReleaseTransactionInContext ? !!initReleaseTransactionInContext.draftObject.eauthElectronicAuth : false
      },
      requestingAccountHolder: '',
      bsloaTransaction: form.isSubmitAndCreateBundledSa,
      nickname: transactionDetails.authorizationNickname ?? '',
      hasCustodianRole: wireInfo.hasCustodianRole
    };
    if (isIra) {
      const fedTaxPercent = +(transactionDetails.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(transactionDetails.stateTaxPercent?.replace('%', '') ?? '0');

      request.iraTransactionRequest = {
        distributionReason: {
          code: "",
          subCode: "",
          description: "",
          isTaxExempt: false
        },
        stateTaxPercentage: +(transactionDetails.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(transactionDetails.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: true
      }
    }
    if (isTwoStep) {
      request.intermediary = {
        fiRole: "CdtrAgt",
        fiSequenceNo: 0,
        identifierList: [
          {
            identifierType: "Account",
            identifierValue: transactionDetails.intermediaryAccountNumber
          }
        ],
        name: transactionDetails.intermediaryName,
        postalAddress: {
          department: transactionDetails.intermediaryDepartment,
          streetName: transactionDetails.intermediaryStreetName,
          buildingNumber: transactionDetails.intermediaryStreetNumber,
          buildingName: transactionDetails.intermediaryBuildingName,
          floor: transactionDetails.intermediaryFloor,
          room: transactionDetails.intermediaryApartmentSuiteEtc,
          postCode: transactionDetails.intermediaryZip,
          townName: transactionDetails.intermediaryCity,
          townLocationName: "",
          districtName: "",
          countrySubDivision: transactionDetails.intermediaryState,
          country: transactionDetails.intermediaryCountry,
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      }
    }
    return request;
  }

  private getCountryCode(countryList: Country[], countryCodeInfo: string): string {
    return countryList.find(c => c.description === countryCodeInfo)?.code ?? countryCodeInfo;
  }

  isEmptyNullUndefined(value: string): boolean {
    return (value == null || value == undefined || value.trim().length === 0);
  }

  createWireVerifyRequestV3(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    wireInfo: WireInfo,
    modifiedPayeeList: ModifiedPayeeNames[],
    paidForInfo: PaidFor,
    initReleaseTransactionInContext: DraftListAttributes
  ): WireVerifyRequestV3 {
    let transactionDetails = form.transactionDetails as WireTransactionDetails;
    let isIra = accountInfo.classifications.isIra;
    let extraPrompts = form.extraPrompts as WireExtraPrompts;
    const splitAuthId = extraPrompts?.standingAuthId?.split('-') ?? [];
    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';
    let isTwoStep = transactionDetails.intermediarySelection === 'yes';
    let isThreeStep = transactionDetails.isThreeStep;
    let isFourStep = transactionDetails.isFourStep;
    const isUseSloa = extraPrompts?.useStandingAuth === 'yes' || extraPrompts.useBundledStandingAuth === 'yes' ? true : false;
    const isFirstPartyModified = this.isFirstPartyModifiedIso(
      transactionDetails.recipient,
      transactionDetails.name,
      transactionDetails.isoModifiedPayeeName ?? '',
      modifiedPayeeList,
      isUseSloa,
      initReleaseTransactionInContext
    );

    if (splitAuthId.length > 1) {
      cashieringRequestId = splitAuthId[1];
      cashieringRequestSecondId = splitAuthId[2];
    }
    let receivingBankCity = '';
    let receivingBankState = '';

    const receivingBankCityState = transactionDetails?.receivingBankAddress1?.toString().split(' ');
    if (receivingBankCityState?.length > 1) {
      receivingBankCity = receivingBankCityState[0];
      receivingBankState = receivingBankCityState[1];
    }

    let request: WireVerifyRequestV3 = {
      draftId: initReleaseTransactionInContext ? initReleaseTransactionInContext.draftId : '',
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
      processDate: this.datepipe.transform(
        transactionDetails.selectedDate,
        'yyyy-MM-dd'
      ) ?? '',
      frequency: extraPrompts?.useStandingAuth === 'yes' || extraPrompts?.useBundledStandingAuth === 'yes' ? 'ONREQST' : 'ONETIME',
      amount: currencyStringToNumber(transactionDetails.amount),
      isThirdParty: transactionDetails.recipient === 'thirdParty',
      isFirstPartyModified: isFirstPartyModified,
      isEscrow: transactionDetails.isEscrow,
      isUseSloa: isUseSloa,
      nickname: wireInfo?.nickname ?? '',
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      beneficiaryAccount: wireInfo?.isIrsAba ? '' : transactionDetails.toAccount,
      beneficiaryBankId: wireInfo?.isFedWire ? transactionDetails.aba : transactionDetails.receivingBankIdentifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue,
      clientAttested: true,
      beneficiary: {
        partyRole: "Cdtr",
        partySequenceNo: 0,
        partyType: "Individual",
        identifierList: [
            {
                identifierType: transactionDetails?.receivingAccountHolderIdentifierList[0]?.identifierType ?? "Account",
                identifierValue: transactionDetails.toAccount
            }
        ],
        name: transactionDetails.name,
        postalAddress: {
          department: transactionDetails.receivingAccountHolderDepartment,
          streetName: transactionDetails.receivingAccountHolderStreetName,
          buildingNumber: transactionDetails.receivingAccountHolderStreetNumber,
          buildingName: transactionDetails.receivingAccountHolderBuildingName,
          floor: transactionDetails.receivingAccountHolderFloor,
          room: transactionDetails.receivingAccountHolderApartmentSuiteEtc,
          postCode: transactionDetails.receivingAccountHolderZip,
          townName: transactionDetails.receivingAccountHolderCity ? transactionDetails.receivingAccountHolderCity : receivingBankCity,
          townLocationName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderTownLocationName : '',
          districtName: transactionDetails.receivingAccountHolderCountry !== 'US' ? transactionDetails.receivingAccountHolderDistrict : '',
          countrySubDivision: transactionDetails.receivingAccountHolderState,
          country: transactionDetails.receivingAccountHolderCountry,
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      },
      beneficiaryBank: {
        fiRole: isTwoStep || isThreeStep || isFourStep ? "IntrmyAgt" : "CdtrAgt",
        fiSequenceNo: isTwoStep || isThreeStep || isFourStep ? 1 : 0,
        identifierList: isUseSloa ? transactionDetails.receivingBankIdentifierList : [
          {
            identifierType: wireInfo?.isFedWire ? "USABA" : "BIC",
            identifierValue: wireInfo?.isFedWire ? transactionDetails.aba : transactionDetails.receivingBankIdentifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue
          }
        ],

        name: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankName) ? wireInfo?.receivingBankDetailsIso?.recBankName : (transactionDetails.receivingBankName ?? transactionDetails.receivingBankName1),
        postalAddress: {
          department: "",
          streetName: "",
          buildingNumber: "",
          buildingName: "",
          floor: "",
          room: "",
          postCode: "",
          townLocationName: "",
          districtName: "",
          townName: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName) ? wireInfo?.receivingBankDetailsIso?.recBankAddress?.townName : (transactionDetails.receivingBankCity ?? receivingBankCity),
          countrySubDivision: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision) ? wireInfo?.receivingBankDetailsIso?.recBankAddress?.countrySubDivision : (transactionDetails.receivingBankState ?? receivingBankState),
          country: !this.isEmptyNullUndefined(wireInfo?.receivingBankDetailsIso?.recBankAddress?.country) ? this.getCountryCode(wireInfo?.countryList, wireInfo?.receivingBankDetailsIso?.recBankAddress?.country) : transactionDetails.receivingBankCountry,
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        },
      },
      taxRemittance: {
        taxId: transactionDetails.taxPayerId ?? null,
        taxMonthCode: transactionDetails.taxMonth ?? null,
        taxTypeCode: transactionDetails.taxTypeCode ?? null,
        taxYear: transactionDetails.taxYear ?? null,
        additionalInfo: transactionDetails.transmissionNotes ?? null
      },
      isIrsAba: wireInfo?.isIrsAba ?? false,
      note: transactionDetails.transmissionNotes ? transactionDetails.transmissionNotes.trim() : '',
      isProcessDateExtended: wireInfo.processDateFields.isExtendedProcessingWindow,
      caseDetails: {
        /*
        There can be scenario when we have toggled createsloa button on and off on review page
        and clicked on edit, and now want to make Wire VerifyV3 call
        in this case we should only be sending MMWI value in the payload
        which is present at 0th position on comma seperated WIreInfo caseKey
        */
        caseKey: wireInfo?.caseKey?.split(",")[0] != undefined ? wireInfo?.caseKey?.split(",")[0] : wireInfo?.caseKey ?? '',
        caseId: wireInfo?.caseId?.split(",")[0] != undefined ? wireInfo?.caseId?.split(",")[0] : wireInfo?.caseId ?? '',
        isElectronicAuthorization: initReleaseTransactionInContext ? !!initReleaseTransactionInContext.draftObject.eauthElectronicAuth : false
      },
      requestingAccountHolder: extraPrompts?.requestingAccountHolderId ? extraPrompts?.requestingAccountHolderId : '',
      bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
      hasCustodianRole: wireInfo.hasCustodianRole
    };

    if (accountInfo.classifications.isPtrs) {
      request.isTrust = accountInfo.classifications.isPtrs,
        request.trustIncmPct = transactionDetails.trustIncomePercent,
        request.trustPrinPct = transactionDetails.trustPrincipalPercent,
        request.tranMemo = transactionDetails.trustComment,
        request.trustTaxCategory = transactionDetails.trustTaxCategory,
        request.trustTaxCode = transactionDetails.trustTaxCode,
        request.paidFor = paidForInfo.individuals.find(individual => (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) && (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))) ?? new PaidForDetails()
    }

    if (isIra) {
      const fedTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0');

      request.iraTransactionRequest = {
        distributionReason: wireInfo.distributionReasonMap.find(elem => elem.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)!,
        stateTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: transactionDetails.amountType === 'gross',
      }
    }

    if (isTwoStep || isThreeStep || isFourStep) {
      request.intermediary = {
        fiRole: "CdtrAgt",
        fiSequenceNo: 0,
        identifierList: request.frequency == 'ONETIME' ? [
          {
            identifierType: "Account",
            identifierValue: transactionDetails.intermediaryAccountNumber
          }
        ] : transactionDetails.intermediaryIdentifierList,
        name: transactionDetails.intermediaryName,
        postalAddress: {
          department: transactionDetails.intermediaryDepartment,
          streetName: transactionDetails.intermediaryStreetName,
          buildingNumber: transactionDetails.intermediaryStreetNumber,
          buildingName: transactionDetails.intermediaryBuildingName,
          floor: transactionDetails.intermediaryFloor,
          room: transactionDetails.intermediaryApartmentSuiteEtc,
          postCode: transactionDetails.intermediaryZip,
          townName: transactionDetails.intermediaryCity,
          townLocationName: transactionDetails.intermediaryTownLocationName,
          districtName: transactionDetails.intermediaryDistrict,
          countrySubDivision: transactionDetails.intermediaryState,
          country: transactionDetails.intermediaryCountry,
          concatenatedAddress: transactionDetails.intermediaryConcatenatedAddress
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      }
      if (!wireInfo.isISOCompliant) {
        request.intermediary.phone = transactionDetails.intermediaryPhone
      }
    }
    if (isThreeStep || isFourStep) {
      request.secondIntermediary = {
        fiRole: "IntrmyAgt",
        fiSequenceNo: 2,
        identifierList: transactionDetails.secondIntermediaryIdentifierList,
        name: transactionDetails.secondIntermediaryName,
        postalAddress: {
          department: transactionDetails.secondIntermediaryDepartment,
          streetName: transactionDetails.secondIntermediaryStreetName,
          buildingNumber: transactionDetails.secondIntermediaryStreetNumber,
          buildingName: transactionDetails.secondIntermediaryBuildingName,
          floor: transactionDetails.secondIntermediaryFloor,
          room: transactionDetails.secondIntermediaryApartmentSuiteEtc,
          postCode: transactionDetails.secondIntermediaryZip,
          townName: transactionDetails.secondIntermediaryCity,
          townLocationName: transactionDetails.secondIntermediaryTownLocationName,
          districtName: transactionDetails.secondIntermediaryDistrict,
          countrySubDivision: transactionDetails.secondIntermediaryState,
          country: transactionDetails.secondIntermediaryCountry,
          concatenatedAddress: transactionDetails.secondIntermediaryConcatenatedAddress
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      }
    }
    if (isFourStep) {
      request.thirdIntermediary = {
        fiRole: "IntrmyAgt",
        fiSequenceNo: 3,
        identifierList: transactionDetails.thirdIntermediaryIdentifierList,
        name: transactionDetails.thirdIntermediaryName,
        postalAddress: {
          department: transactionDetails.thirdIntermediaryDepartment,
          streetName: transactionDetails.thirdIntermediaryStreetName,
          buildingNumber: transactionDetails.thirdIntermediaryStreetNumber,
          buildingName: transactionDetails.thirdIntermediaryBuildingName,
          floor: transactionDetails.thirdIntermediaryFloor,
          room: transactionDetails.thirdIntermediaryApartmentSuiteEtc,
          postCode: transactionDetails.thirdIntermediaryZip,
          townName: transactionDetails.thirdIntermediaryCity,
          townLocationName: transactionDetails.thirdIntermediaryTownLocationName,
          districtName: transactionDetails.thirdIntermediaryDistrict,
          countrySubDivision: transactionDetails.thirdIntermediaryState,
          country: transactionDetails.thirdIntermediaryCountry,
          concatenatedAddress: transactionDetails.thirdIntermediaryConcatenatedAddress
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      }
    }
    return request;
  }

  createWireSloaVerifyRequestV3FromCreateTransaction(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    wireInfo: WireInfo,
    modifiedPayeeList: ModifiedPayeeNames[],
    paidForInfo: PaidFor,
    initReleaseTransactionInContext: DraftListAttributes,
    isSubmitAndCreateSloa: boolean = true
  ): WireVerifyRequestV3 {
    let transactionDetails = form.transactionDetails as WireTransactionDetails;
    let isIra = accountInfo.classifications.isIra;
    let extraPrompts = form.extraPrompts as WireExtraPrompts;
    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';
    let isTwoStep = wireInfo?.toDetails?.isoIntermediary != undefined;
    let isThirdParty = wireInfo?.toDetails?.isoBeneficiary?.identifierList?.filter(x => x.identifierType?.toLowerCase() == "firstorthirdparty")[0]?.identifierValue?.toLowerCase() == "thirdparty";
    const isFirstPartyModified = this.isFirstPartyModifiedIso(
      isThirdParty ? 'thirdParty' : 'firstParty',
      wireInfo?.receivingAccountHolderIso?.name ?? '',
      wireInfo?.toDetails?.isoBeneficiary?.name ?? '',
      modifiedPayeeList,
      false,
      initReleaseTransactionInContext
    );

    let request: WireVerifyRequestV3 = {
      draftId: initReleaseTransactionInContext ? initReleaseTransactionInContext.draftId : '',
      isCreateSloa: true,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      processDate: DateTime.now().toFormat('yyyy-MM-dd'),
      frequency: 'ONREQST',
      amount: 0,
      isSubmitAndCreateSloa: isSubmitAndCreateSloa,
      isThirdParty: isThirdParty,
      isFirstPartyModified: isFirstPartyModified,
      isEscrow: wireInfo?.isEscrow,
      isUseSloa: false,
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      beneficiaryAccount: wireInfo?.isIrsAba ? '' : wireInfo?.toDetails?.accountNumber,
      beneficiaryBankId: wireInfo?.isFedWire ? transactionDetails.aba : transactionDetails.receivingBankIdentifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue,
      clientAttested: true,
      beneficiary: {
        partyRole: "Cdtr",
        partySequenceNo: 0,
        partyType: "Individual",
        identifierList: [
          {
            identifierType: "Account",
            identifierValue: wireInfo?.toDetails?.isoBeneficiary?.accountNumber?.identifierValue ?? ''
          }
        ],
        name: wireInfo?.toDetails?.isoBeneficiary?.name ?? '',
        postalAddress: {
          department: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.department ?? '',
          streetName: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.streetName ?? '',
          buildingNumber: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.buildingNumber ?? '',
          buildingName: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.buildingName ?? '',
          floor: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.floor ?? '',
          room: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.room ?? '',
          postCode: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.postCode ?? '',
          townName: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.townName ?? '',
          townLocationName: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.townLocationName ?? '',
          districtName: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.districtName ?? '',
          countrySubDivision: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.countrySubDivision ?? '',
          country: wireInfo?.toDetails?.isoBeneficiary?.postalAddress?.country ?? '',
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      },
      beneficiaryBank: {
        fiRole: isTwoStep ? "IntrmyAgt" : "CdtrAgt",
        fiSequenceNo: isTwoStep ? 1 : 0,
        identifierList: [
          {
            identifierType: wireInfo?.isFedWire ? "USABA" : "BIC",
            identifierValue: wireInfo?.isFedWire ? transactionDetails.aba : transactionDetails.receivingBankIdentifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue
          }
        ],

        name: wireInfo?.toDetails?.isoBeneficiaryBank?.name ?? '',
        postalAddress: {
          department: "",
          streetName: "",
          buildingNumber: "",
          buildingName: "",
          floor: "",
          room: "",
          postCode: "",
          townName: wireInfo?.toDetails?.isoBeneficiaryBank?.postalAddress?.townName ?? '',
          townLocationName: "",
          districtName: "",
          countrySubDivision: wireInfo?.toDetails?.isoBeneficiaryBank?.postalAddress?.countrySubDivision ?? '',
          country: wireInfo?.toDetails?.isoBeneficiaryBank?.postalAddress?.country ?? '',
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        },
      },
      taxRemittance: {
        taxId: wireInfo.taxRemittance?.taxId ?? null,
        taxMonthCode: wireInfo.taxRemittance?.taxMonthCode ?? null,
        taxTypeCode: wireInfo.taxRemittance?.taxTypeCode ?? null,
        taxYear: wireInfo.taxRemittance?.taxYear ?? null,
        additionalInfo: wireInfo.taxRemittance?.additionalInfo ?? null
      },
      isIrsAba: wireInfo?.isIrsAba ?? false,
      note: wireInfo?.transmissionNotes,
      isProcessDateExtended: wireInfo?.processDateFields.isExtendedProcessingWindow,
      caseDetails: {
        caseKey: '',
        caseId: '',
        isElectronicAuthorization: initReleaseTransactionInContext ? !!initReleaseTransactionInContext.draftObject.eauthElectronicAuth : false
      },
      requestingAccountHolder: '',
      bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
      hasCustodianRole: wireInfo?.hasCustodianRole
    };

    if (isIra) {
      const fedTaxPercent = +(wireInfo?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(wireInfo?.stateTaxPercent?.replace('%', '') ?? '0');

      request.iraTransactionRequest = {
        distributionReason: {
          code: wireInfo?.distributionReason?.code,
          subCode: wireInfo?.distributionReason?.subCode,
          description: wireInfo?.distributionReason?.description,
          isTaxExempt: wireInfo?.distributionReason?.isTaxExempt
        },
        stateTaxPercentage: stateTaxPercent,
        fedTaxPercentage: fedTaxPercent,
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: true,
      }
    }

    if (isTwoStep) {
      request.intermediary = {
        fiRole: "CdtrAgt",
        fiSequenceNo: 0,
        identifierList: [
          {
            identifierType: "Account",
            identifierValue: wireInfo?.toDetails?.isoIntermediary?.accountNumber?.identifierValue ?? ''
          }
        ],
        name: wireInfo?.toDetails?.isoIntermediary?.name ?? '',
        postalAddress: {
          department: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.department ?? '',
          streetName: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.streetName ?? '',
          buildingNumber: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.buildingNumber ?? '',
          buildingName: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.buildingName ?? '',
          floor: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.floor ?? '',
          room: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.room ?? '',
          postCode: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.postCode ?? '',
          townName: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.townName ?? '',
          townLocationName: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.townLocationName ?? '',
          districtName: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.districtName ?? '',
          countrySubDivision: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.countrySubDivision ?? '',
          country: wireInfo?.toDetails?.isoIntermediary?.postalAddress?.country ?? '',
          concatenatedAddress: ""
        },
        legacy: {
          name1: '',
          name2: '',
          address1: '',
          address2: '',
          country: ''
        }
      }
      if (!wireInfo.isISOCompliant) {
        request.intermediary.phone = transactionDetails.intermediaryPhone
      }
    }
    return request;
  }

  createWireAcknowledgeRequestV3(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    wireInfo: WireInfo,
    acknowledgementConsentDateTime: string,
    paidForInfo: PaidFor,
    eAuthInfo: EAuthInfo,
    modifiedPayeeList: ModifiedPayeeNames[],
    initReleaseTransactionInContext: DraftListAttributes,
    canNotBeSubmitAndCreateSloa: boolean = false
  ): WireAcknowledgeRequestV3 {
    let transactionDetails = form.transactionDetails as WireTransactionDetails;
    let extraPrompts = form.extraPrompts as WireExtraPrompts;
    let isIra = accountInfo.classifications.isIra;
    let isPtrs = accountInfo.classifications.isPtrs;
    this.store.select(selectSubmitAndCreateToggledOn)
      .pipe(takeUntil(this.destroy$))
      .subscribe((submitAndCreateToggledOn) => {
        wireInfo.submitAndCreateToggledOn = submitAndCreateToggledOn;
      });
    let eauthCustomer: {
      customerId: string,
      emailId: string,
      firstName: string,
      lastName: string,
      loginIdEnrollStatus: string,
      organizationName: string,
      typeCode: string,
      customerRestrictionReason: string,
      accountRoles: string[],
      customerWebEnrolled: boolean,
      isAccountWebEnabled: boolean,
      isCustomerWebEnrolled: boolean,
      isDisplayAllowed: boolean,
      isCustomerEAuthEligible: boolean,
      eauthInd: boolean
    } = {
      customerId: '',
      emailId: '',
      firstName: '',
      lastName: '',
      loginIdEnrollStatus: '',
      organizationName: '',
      typeCode: '',
      customerRestrictionReason: '',
      accountRoles: [],
      customerWebEnrolled: false,
      isAccountWebEnabled: false,
      isCustomerWebEnrolled: false,
      isDisplayAllowed: false,
      isCustomerEAuthEligible: false,
      eauthInd: false
    };
    const isUseSloa = extraPrompts?.useStandingAuth === 'yes' || extraPrompts?.useBundledStandingAuth === 'yes' ? true : false;
    const isFirstPartyModified = this.isFirstPartyModifiedIso(
      transactionDetails.recipient,
      transactionDetails.name,
      transactionDetails.isoModifiedPayeeName ?? '',
      modifiedPayeeList,
      false,
      initReleaseTransactionInContext
    );

    if (eAuthInfo.isAccountHolderSelected) {
      const selectedCustomer = wireInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === eAuthInfo.selectedAccountHolder[0]);
      if (selectedCustomer) {
        eauthCustomer.customerId = selectedCustomer?.customerId;
        eauthCustomer.emailId = selectedCustomer?.emailId;
        eauthCustomer.firstName = selectedCustomer?.firstName;
        eauthCustomer.lastName = selectedCustomer?.lastName;
        eauthCustomer.loginIdEnrollStatus = selectedCustomer?.loginIdEnrollStatus;
        eauthCustomer.organizationName = selectedCustomer?.organizationName;
        eauthCustomer.typeCode = selectedCustomer?.typeCode;
        eauthCustomer.customerRestrictionReason = selectedCustomer?.customerRestrictionReason;
        eauthCustomer.accountRoles = selectedCustomer?.accountRoles;
        eauthCustomer.customerWebEnrolled = selectedCustomer?.isCustomerWebEnrolled;
        eauthCustomer.isAccountWebEnabled = selectedCustomer?.isAccountWebEnabled;
        eauthCustomer.isCustomerWebEnrolled = selectedCustomer?.customerWebEnrolled.toLowerCase() === 'true';
      }
    }

    const splitAuthId = extraPrompts?.standingAuthId?.split('-') ?? [];
    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';

    if (splitAuthId.length > 1) {
      cashieringRequestId = splitAuthId[1];
      cashieringRequestSecondId = splitAuthId[2];
    }

    let request: WireAcknowledgeRequestV3 = {
      isIrsAba: wireInfo?.isIrsAba ?? false,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
      // TODO: Needs updating once we decide on how we want to handle the futureDatedWires federal holiday process date scenario
      originalRequestedDate: wireInfo.requestedDate,
      processDate: this.datepipe.transform(
        transactionDetails.selectedDate,
        'yyyy-MM-dd'
      ) ?? '',
      draftId: wireInfo?.authResponse?.draftId ?? '',
      frequency: extraPrompts?.useStandingAuth === 'yes' || extraPrompts?.useBundledStandingAuth === 'yes' ? 'ONREQST' : 'ONETIME',
      amount: currencyStringToNumber(transactionDetails.amount),
      isThirdParty: transactionDetails.recipient === 'thirdParty',
      isFirstPartyModified: isFirstPartyModified,
      isEscrow: transactionDetails.isEscrow,
      beneficiaryBankId: wireInfo?.isFedWire ? transactionDetails.aba : transactionDetails.receivingAccountHolderIdentifierList.filter(x => x.identifierType == "BIC")[0]?.identifierValue,
      beneficiaryAccount: wireInfo?.isIrsAba ? '' : transactionDetails.toAccount,
      note: transactionDetails.transmissionNotes ? transactionDetails.transmissionNotes.trim() : '',
      caseDetails: {
        /* 
        wireInfo will not get updated when we turn off the create sloa toggle button on review page, so
        if toggle is on then need to send both MMWI & MMWS casekey's
        if toggle is off then need to send only MMWI which is on 0th position of wireInfo
        and that need to be send in payload
        */
        caseKey: wireInfo.submitAndCreateToggledOn ? wireInfo?.caseKey : wireInfo?.caseKey?.split(",")[0],
        caseId: wireInfo.submitAndCreateToggledOn ? wireInfo?.caseId : wireInfo?.caseId?.split(",")[0],
        isElectronicAuthorization: eAuthInfo.isAccountHolderSelected
      },
      sloaEAuthClients: this.mapCustomerListSloaV3(wireInfo.submitAndCreateToggledOn, [eauthCustomer], wireInfo.eAuthModel),
      transactionEAuthClient: eauthCustomer,
      clientAttested: true,
      attestationSubmittedBy: wireInfo.attestationSubmittedBy,
      bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      taxRemittance: {
        taxId: wireInfo.taxRemittance?.taxId ?? null,
        taxMonthCode: wireInfo.taxRemittance?.taxMonthCode ?? null,
        taxTypeCode: wireInfo.taxRemittance?.taxTypeCode ?? null,
        taxYear: wireInfo.taxRemittance?.taxYear ?? null,
        additionalInfo: wireInfo.taxRemittance?.additionalInfo ?? null
      },
      isUseSloa: isUseSloa,
      nickname: wireInfo?.nickname ?? '',
      initiatedBy: "",
      isCreateSloa: false,
      isSubmitAndCreateSloa: canNotBeSubmitAndCreateSloa ? false : wireInfo.submitAndCreateToggledOn,
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      needsAddressUpdate: wireInfo.missingThirdPartyAddress || wireInfo.missingIntermediaryAddress,
      affirmationRequired: wireInfo.affirmationRequired,
    };

    if (isIra) {
      const fedTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0');

      request.iraTransactionRequest = {
        distributionReason: wireInfo.distributionReasonMap.find(elem => elem.description === transactionDetails.taxWithholdingsFormGroup?.distributionReason)!,
        stateTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(transactionDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: transactionDetails.amountType === 'gross',
      }
    };
    function getPaidForContact() {
      if (initReleaseTransactionInContext?.draftObject?.paidFor) {
        return initReleaseTransactionInContext.draftObject.paidFor;
      }
      const foundIndividual = paidForInfo.individuals.find(individual =>
        (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.name.toLocaleLowerCase())) &&
        (transactionDetails.trustPaidFor?.toLocaleLowerCase().includes(individual.role.toLocaleLowerCase()))
      );
      return foundIndividual ?? new PaidForDetails();
    }

    if (isPtrs) {
      request.trustFieldsRequest = {
        incomePercent: Number(transactionDetails.trustIncomePercent?.replace('%', '')),
        principalPercent: Number(transactionDetails.trustPrincipalPercent?.replace('%', '')),
        paidForContact: getPaidForContact(),
        settlementDate: transactionDetails.selectedDate,
        tradeDate: getTradeDate(),
        taxCode: transactionDetails.trustTaxCode ?? '',
        taxCategory: transactionDetails.trustTaxCategory ?? '',
        comment: transactionDetails.trustComment ?? '',
        payeeInfo: {
          beneficiaryAccount: transactionDetails.toAccount,
          beneficiaryEscrowName1: transactionDetails.receivingAccountHolderName,
        }
      }
    }

    return request;
  }

  createStandingAuthWireAcknowledgeRequestV3(
    accountInfo: AccountInfoState,
    form: StandingAuthorizationForm,
    wireInfo: WireInfo,
    acknowledgementConsentDateTime: string,
    eAuthInfo: EAuthInfo,
    modifiedPayeeList: ModifiedPayeeNames[],
    initReleaseTransactionInContext: DraftListAttributes
  ): WireAcknowledgeRequestV3 {
    let standingAuthDetails = form.standingAuthorizationDetails as WireStandingAuthDetails;
    let isIra = accountInfo.classifications.isIra;
    let eauthCustomerList: RelatedCustomer[] = [];

    if (eAuthInfo.isAccountHolderSelected) {
      eAuthInfo.selectedAccountHolder.forEach(selectedAccount => {
        let selectedCustomer = wireInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === selectedAccount);
        eauthCustomerList.push({
          customerId: selectedCustomer?.customerId ?? '',
          emailId: selectedCustomer?.emailId ?? '',
          firstName: selectedCustomer?.firstName ?? '',
          lastName: selectedCustomer?.lastName ?? '',
          loginIdEnrollStatus: selectedCustomer?.loginIdEnrollStatus ?? '',
          organizationName: selectedCustomer?.organizationName ?? '',
          typeCode: selectedCustomer?.typeCode ?? '',
          customerRestrictionReason: selectedCustomer?.customerRestrictionReason ?? '',
          accountRoles: selectedCustomer?.accountRoles ?? [],
          isAccountWebEnabled: selectedCustomer?.isAccountWebEnabled ?? false,
          isCustomerWebEnrolled: selectedCustomer?.customerWebEnrolled.toLowerCase() === 'true',
          isDisplayAllowed: false,
          isCustomerEAuthEligible: false,
          eauthInd: false
        })
      })
    }

    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';

    let request: WireAcknowledgeRequestV3 = {
      isIrsAba: wireInfo?.isIrsAba ?? false,  
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      // TODO: Needs updating once we decide on how we want to handle the futureDatedWires federal holiday process date scenario
      originalRequestedDate: wireInfo.requestedDate,
      processDate: DateTime.now().toFormat('yyyy-MM-dd'),
      frequency: 'ONREQST',
      amount: 0,
      isThirdParty: standingAuthDetails.recipient === 'thirdParty',
      isFirstPartyModified: this.isFirstPartyModifiedIso(
        standingAuthDetails.recipient,
        standingAuthDetails.name,
        standingAuthDetails.isoModifiedPayeeName ?? '',
        modifiedPayeeList,
        false,
        initReleaseTransactionInContext
      ),
      isEscrow: standingAuthDetails.isEscrow,
      beneficiaryBankId: wireInfo.isFedWire ? standingAuthDetails.aba : standingAuthDetails.bankID,
      beneficiaryAccount: wireInfo?.isIrsAba ? '' : standingAuthDetails.toAccount,
      note: standingAuthDetails.transmissionNotes.trim(),
      caseDetails: {
        caseKey: wireInfo.caseKey,
        caseId: wireInfo.caseId,
        isElectronicAuthorization: eAuthInfo.isAccountHolderSelected
      },
      sloaEAuthClients: eauthCustomerList,
      transactionEAuthClient: eauthCustomerList[0],
      clientAttested: true,
      attestationSubmittedBy: wireInfo.attestationSubmittedBy,
      bsloaTransaction: form.isSubmitAndCreateBundledSa ?? false,
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      isUseSloa: false,
      nickname: standingAuthDetails.authorizationNickname,
      initiatedBy: "",
      isCreateSloa: true,
      isSubmitAndCreateSloa: false,
      taxRemittance: {
        taxId: wireInfo.taxRemittance?.taxId ?? null,
        taxMonthCode: wireInfo.taxRemittance?.taxMonthCode ?? null,
        taxTypeCode: wireInfo.taxRemittance?.taxTypeCode ?? null,
        taxYear: wireInfo.taxRemittance?.taxYear ?? null,
        additionalInfo: wireInfo.taxRemittance?.additionalInfo ?? null
      },
      draftId: wireInfo?.authResponse?.draftId ?? '',
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      needsAddressUpdate: wireInfo.missingThirdPartyAddress || wireInfo.missingIntermediaryAddress,
    };

    if (isIra) {
      const fedTaxPercent = +(standingAuthDetails?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(standingAuthDetails?.stateTaxPercent?.replace('%', '') ?? '0');

      request.iraTransactionRequest = {
        distributionReason: wireInfo.distributionReasonMap.find(elem => elem.description === standingAuthDetails.taxWithholdingsFormGroup?.distributionReason)!,
        stateTaxPercentage: +(standingAuthDetails?.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(standingAuthDetails?.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: true,
      }
    };

    return request;
  }

  mapCustomerListSloaV3(isCreateSloa: boolean, selectedEauthCustomer: RelatedCustomer[], eauthModel: EAuthModel): RelatedCustomer[] {
    let approvers: RelatedCustomer[] = [];
    if (isCreateSloa) {
        approvers = selectedEauthCustomer;
    }
    return approvers;
  }

  isFirstPartyModifiedIso(
    recipient: string,
    name: string,
    isoModifiedPayeeName: string,
    modifiedPayeeList: ModifiedPayeeNames[],
    isUseSloa: boolean,
    inrTranasctionInContext: DraftListAttributes
  ): boolean {
    if (!!inrTranasctionInContext.draftId.trim()) {
      let isFirstPartyModified = inrTranasctionInContext.draftObject.isFirstPartyModified?.toString();
      return isFirstPartyModified === 'Y' || isFirstPartyModified === 'true';
    }
    if (recipient !== 'firstParty' || isUseSloa) return false;
    if (isoModifiedPayeeName) {
      const payee = modifiedPayeeList.find(payee => payee.name1 === isoModifiedPayeeName);
      if (payee) {
        return !payee.isPrimary;
      }
      return true;
    } else {
      const payee = modifiedPayeeList.find(payee => payee.name1 === name);
      if (payee) {
        return payee.pending;
      }
      return false;
    }

  }

  createStandingAuthWireAcknowledgeRequestV3ForCustomRulesSplitTransaction(
    accountInfo: AccountInfoState,
    form: TransactionForm,
    wireInfo: WireInfo,
    acknowledgementConsentDateTime: string,
    eAuthInfo: EAuthInfo,
    modifiedPayeeList: ModifiedPayeeNames[],
    initReleaseTransactionInContext: DraftListAttributes
  ): WireAcknowledgeRequestV3 {
    let standingAuthDetails = form.transactionDetails as WireTransactionDetails;
    let extraPrompts = form.extraPrompts as WireExtraPrompts;
    let isIra = accountInfo.classifications.isIra;
    let eauthCustomerList: RelatedCustomer[] = [];

    if (eAuthInfo.isAccountHolderSelected) {
      eAuthInfo.selectedAccountHolder.forEach(selectedAccount => {
        let selectedCustomer = wireInfo.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList.find(customer => customer.customerId === selectedAccount);
        eauthCustomerList.push({
          customerId: selectedCustomer?.customerId ?? '',
          emailId: selectedCustomer?.emailId ?? '',
          firstName: selectedCustomer?.firstName ?? '',
          lastName: selectedCustomer?.lastName ?? '',
          loginIdEnrollStatus: selectedCustomer?.loginIdEnrollStatus ?? '',
          organizationName: selectedCustomer?.organizationName ?? '',
          typeCode: selectedCustomer?.typeCode ?? '',
          customerRestrictionReason: selectedCustomer?.customerRestrictionReason ?? '',
          accountRoles: selectedCustomer?.accountRoles ?? [],
          isAccountWebEnabled: selectedCustomer?.isAccountWebEnabled ?? false,
          isCustomerWebEnrolled: selectedCustomer?.customerWebEnrolled.toLowerCase() === 'true',
          isDisplayAllowed: false,
          isCustomerEAuthEligible: false,
          eauthInd: false
        })
      })
    }

    let cashieringRequestId = '';
    let cashieringRequestSecondId = '';

    let request: WireAcknowledgeRequestV3 = {
      isIrsAba: wireInfo?.isIrsAba ?? false,
      subAccountId: accountInfo.accountId,
      masterAccountId: accountInfo.masterAccount,
      // TODO: Needs updating once we decide on how we want to handle the futureDatedWires federal holiday process date scenario
      originalRequestedDate: wireInfo.requestedDate,
      processDate: DateTime.now().toFormat('yyyy-MM-dd'),
      frequency: 'ONREQST',
      amount: 0,
      isThirdParty: standingAuthDetails?.recipient === 'thirdParty',
      isFirstPartyModified: this.isFirstPartyModifiedIso(
        standingAuthDetails.recipient,
        standingAuthDetails.name,
        standingAuthDetails.isoModifiedPayeeName ?? '',
        modifiedPayeeList,
        false,
        initReleaseTransactionInContext
      ),
      isEscrow: standingAuthDetails.isEscrow,
      beneficiaryBankId: wireInfo.isFedWire ? standingAuthDetails.aba : standingAuthDetails.bankID,
      beneficiaryAccount: standingAuthDetails.toAccount,
      note: standingAuthDetails.transmissionNotes.trim(),
      caseDetails: {
        caseKey: wireInfo.caseKey,
        caseId: wireInfo.caseId,
        isElectronicAuthorization: eAuthInfo.isAccountHolderSelected
      },
      sloaEAuthClients: eauthCustomerList,
      transactionEAuthClient: eauthCustomerList[0],
      clientAttested: true,
      attestationSubmittedBy: wireInfo.attestationSubmittedBy,
      bsloaTransaction: extraPrompts.useStandingAuth === 'bundled',
      sloaId: {
        cashieringRequestId,
        cashieringRequestSecondId
      },
      isUseSloa: false,
      nickname: wireInfo.nickname ?? '',
      initiatedBy: "",
      isCreateSloa: true,
      isSubmitAndCreateSloa: false,
      taxRemittance: {
        taxId: wireInfo.taxRemittance?.taxId ?? null,
        taxMonthCode: wireInfo.taxRemittance?.taxMonthCode ?? null,
        taxTypeCode: wireInfo.taxRemittance?.taxTypeCode ?? null,
        taxYear: wireInfo.taxRemittance?.taxYear ?? null,
        additionalInfo: wireInfo.taxRemittance?.additionalInfo ?? null
      },
      draftId: wireInfo?.authResponse?.draftId ?? '', 
      acknowledgementConsentDateTime: acknowledgementConsentDateTime,
      needsAddressUpdate: wireInfo.missingThirdPartyAddress || wireInfo.missingIntermediaryAddress,
    };

    if (isIra) {
      const fedTaxPercent = +(standingAuthDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0');
      const stateTaxPercent = +(standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0');
      request.iraTransactionRequest = {
        distributionReason: wireInfo.distributionReasonMap.find(elem => elem.description === standingAuthDetails.taxWithholdingsFormGroup?.distributionReason)!,
        stateTaxPercentage: +(standingAuthDetails.taxWithholdingsFormGroup?.stateTaxPercent?.replace('%', '') ?? '0'),
        fedTaxPercentage: +(standingAuthDetails.taxWithholdingsFormGroup?.fedTaxPercent?.replace('%', '') ?? '0'),
        isWithholdFedTax: fedTaxPercent !== 0,
        isWithholdStateTax: stateTaxPercent !== 0,
        isGrossAmt: true,
      }
    };

    return request;
  }

  callGetWireDetailsV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WireActions.callWireDetailsV2),
      concatLatestFrom(() => [
        this.store.select(selectInfo),
        this.store.select(selectWireInfo),
        this.store.select(selectIsCreateTransaction),
        this.store.select(selectIsCreateStandingAuth)
      ]),
      exhaustMap(([payload, accountInfo, wireInfo, isCreateTransactionPage, isCreateStandingAuthPage]) => {
        let request: WireDetailsRequest = {
          subAccountId: accountInfo.accountId,
          masterAccountId: accountInfo.masterAccount ?? accountInfo.primaryMaster ?? '',
          abaNumber: wireInfo.receivingBankDetailsIso.recBankAba,
          isCreateSloa: isCreateStandingAuthPage,
          isOnetime: isCreateTransactionPage
        };
        return this.wireService.callWireDetails(request, '2', payload.isPalAccountInContext).pipe(
          map((response: WireDetailsApiResponse) => {
            const detailsResponse = this.mapDetailsResponseToWireInfo(response);
            return WireActions.callwireDetailsV2Successful(detailsResponse);
          }),
          catchError((err: ServiceError) => {
            return of(WireActions.wireDetailsCallFailed(err))
          })
        );
      })
    )
  );

  mapDetailsResponseToWireInfo(wireDetailsResponse: WireDetailsApiResponse): DetailsResponse {
    return {
      abaStatusCode: wireDetailsResponse.abaStatusCode,
      receivingBankDetails: {
        recBankAba: wireDetailsResponse.recBankDetailIso.recBankAba,
        recBankName1: wireDetailsResponse.recBankDetail.recBankName1,
        recBankName2: wireDetailsResponse.recBankDetail.recBankName2,
        recBankAddress1: wireDetailsResponse.recBankDetail.recBankAddress1,
        recBankAddress2: wireDetailsResponse.recBankDetail.recBankAddress2,
        recBankCountry: wireDetailsResponse.recBankDetail.recBankCountry,
        bankPhoneArea: wireDetailsResponse.recBankDetail.bankPhoneArea,
        bankPhoneNum1: wireDetailsResponse.recBankDetail.bankPhoneNum1,
        bankPhoneNum2: wireDetailsResponse.recBankDetail.bankPhoneNum2,
        intermediaryAccount: wireDetailsResponse.recBankDetail.intermediaryAccount,
        intermediaryName1: wireDetailsResponse.recBankDetail.intermediaryName1,
        intermediaryName2: wireDetailsResponse.recBankDetail.intermediaryName2,
        intermediaryAddress1: wireDetailsResponse.recBankDetail.intermediaryAddress1,
        intermediaryAddress2: wireDetailsResponse.recBankDetail.intermediaryAddress2,
        interPhoneArea: wireDetailsResponse.recBankDetail.interPhoneArea,
        interPhoneNum1: wireDetailsResponse.recBankDetail.interPhoneNum1,
        interPhoneNum2: wireDetailsResponse.recBankDetail.interPhoneNum2,
        interPhoneExt: wireDetailsResponse.recBankDetail.interPhoneExt,
      },
      receivingBankDetailsIso: {
        recBankAba: wireDetailsResponse.recBankDetailIso.recBankAba,
        recBankName: wireDetailsResponse.recBankDetailIso.recBankName,
        recBankAddress: {
          townName: wireDetailsResponse.recBankDetailIso.recBankAddress.townName,
          department: "",
          streetName: "",
          buildingNumber: "",
          buildingName: "",
          floor: "",
          room: "",
          postCode: "",
          townLocationName: "",
          districtName: "",
          countrySubDivision: wireDetailsResponse.recBankDetailIso.recBankAddress.countrySubDivision,
          country: wireDetailsResponse.recBankDetailIso.recBankAddress.country,
          concatenatedAddress: ""
        },
        intermediaryAccount: wireDetailsResponse.recBankDetailIso.intermediaryAccount,
        intermediaryName: wireDetailsResponse.recBankDetailIso.intermediaryName,
        intermediaryAddress: {
          department: "",
          streetName: "",
          buildingNumber: "",
          buildingName: "",
          floor: "",
          room: "",
          postCode: "",
          townName: wireDetailsResponse.recBankDetailIso.intermediaryAddress.townName,
          townLocationName: "",
          districtName: "",
          countrySubDivision: wireDetailsResponse.recBankDetailIso.intermediaryAddress.countrySubDivision,
          country: "",
          concatenatedAddress: ""
        },
        recBankPhone: "",
        intermediaryPhone: ""
      },
      receivingBankAba: wireDetailsResponse.receivingBankAba,
      abaMessageFields: {
        errorList: wireDetailsResponse.messageFields.errorList,
        warningList: wireDetailsResponse.messageFields.warningList,
        atsSystemErrors: wireDetailsResponse.messageFields.atsSystemErrors
      },
      isIrsAba: wireDetailsResponse.isIrsAba
    }
  }

  private isPalAccountInContext(): boolean {
    return isPalAccountFlow(
      this.coreService.currentContext?.contextType,
      this.validateWireFlagsService.enablePalWireCreateTransaction()
    );
  }

}
